import toast from "react-hot-toast";
import useRead from "../useRead";
import { checkIfNotEmpty, formatDateWithHyphens } from "../../utility";
import { useState } from "react";
import { useForm } from "react-hook-form";
import usePut from "../usePut";
import { useNavigate } from "react-router-dom";
import { roundToTwoDecimalPlaces } from "../../utility/utils";

const useAddEditUserSalary = ({
  isEdit = false,
  salaryId,
  templateId,
  employeeId,
  username,
  source,
}) => {
  const navigate = useNavigate();

  const [showAddRowModal, setShowAddRowModal] = useState(false);

  const { control, handleSubmit, setValue, register, watch, getValues } =
    useForm({
      defaultValues: {
        earnings: [],
        annual_ctc: 0,
        statutory: {},
      },
    });

  const [othersMonthlyValue, setOthersMonthlyValue] = useState(0);
  const [othersAnnualValue, setOthersAnnualValue] = useState(0);

  const recalculateOthersValue = () => {
    const annualCtc = getValues("annual_ctc") || 0;
    const earningsArr = getValues("earnings") || [];

    const monthlyTotal = earningsArr.reduce((acc, el) => {
      return acc + (parseFloat(el.monthly_value) || 0);
    }, 0);

    const monthlyVal = roundToTwoDecimalPlaces(
      parseFloat(annualCtc) / 12 - monthlyTotal
    );
    setOthersMonthlyValue(monthlyVal);

    // Calculating annual value
    const annualTotal = earningsArr.reduce((acc, el) => {
      return acc + (parseFloat(el.annual_value) || 0);
    }, 0);

    const annualVal = roundToTwoDecimalPlaces(
      parseFloat(annualCtc) - annualTotal
    );
    setOthersAnnualValue(annualVal);
  };

  const toggleAddRowModal = () => {
    setShowAddRowModal(!showAddRowModal);
  };

  const [statutoryComponentsList, setStatutoryComponentsList] = useState([]);

  // Fetch Statutory Components
  const { list: fetchStatutoryRequest } = useRead({
    url: "statutory-types/list",
    method: "GET",
    initialData: {},
    onSuccess: (data) => {
      const resData = data?.data || [];

      setStatutoryComponentsList(resData);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "Error fetching statutory components: " + error.message
      );
    },
  });
  const isFetchingStatutory = fetchStatutoryRequest.isLoading;

  // Fetch saved salary details
  useRead({
    url: "user-salary-components/fetch",
    method: "GET",
    initialData: {
      salary_id: salaryId,
      company_employee_id: employeeId,
    },
    enabled: isEdit && checkIfNotEmpty(salaryId),
    onSuccess: (data) => {
      const earningsArr = data?.earnings || [];

      setValue("earnings", earningsArr);
      setValue("annual_ctc", data?.annual_ctc || 0);
      setValue("from_date", data?.from_date ? new Date(data?.from_date) : null);

      const statutoryData = {};
      (data?.statutory || []).forEach((statutory) => {
        statutoryData[statutory.name] = statutory.selected || false;
      });
      setValue("statutory", statutoryData);

      if (!isEdit) calculateVariableValues();
      recalculateOthersValue();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "Error fetching user salary details: " + error.message
      );
    },
  });

  const [templateName, setTemplateName] = useState("");

  // Fetch template data
  useRead({
    url: "template-salary-components/fetchtemplateData",
    method: "GET",
    enabled: checkIfNotEmpty(templateId),
    initialData: {
      template_id: templateId,
    },
    onSuccess: (data) => {
      const earningsArr = data?.earnings;

      setTemplateName(data?.template_structure?.name || "");

      setValue("annual_ctc", data?.template_structure?.annual_ctc || 0);
      setValue(
        "earnings",
        earningsArr?.map((el) => {
          return {
            ...el,
            component_id: el.component_id,
            name: el.component_name,
            monthly_value: el.monthly_value || 0,
            annual_value: el.annual_value || 0,
            is_yearly_editable:
              parseInt(el.is_yearly_editable) === 1 || el.is_yearly_editable
                ? true
                : false,
            is_monthly_editable:
              parseInt(el.is_monthly_editable) === 1 || el.is_monthly_editable
                ? true
                : false,
          };
        }) || []
      );

      const statutoryData = {};
      (data?.statutory || []).forEach((statutory) => {
        statutoryData[statutory.name] = statutory.selected || false;
      });
      setValue("statutory", statutoryData);

      setOthersAnnualValue(data?.template_structure?.annual_others || 0);
      setOthersMonthlyValue(data?.template_structure?.monthly_others || 0);

      if (!isEdit) calculateVariableValues();
      recalculateOthersValue();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "Error fetching salary components: " + error.message
      );
    },
  });

  //   For adding and removing rows
  const addRows = (newRows) => {
    const updatedEarnings = [
      ...watch("earnings"),
      ...newRows.map((el) => {
        return {
          ...el,
          component_id: el.component_id,
          name: el.name,
          calculation_type: el.calculation_type,
          calculation_value: el.calculation_value || el.value || 0,
          monthly_value: 0,
          annual_value: 0,
          is_yearly_editable:
            parseInt(el.is_yearly_editable) === 1 || el.is_yearly_editable
              ? true
              : false,
          is_monthly_editable:
            parseInt(el.is_monthly_editable) === 1 || el.is_monthly_editable
              ? true
              : false,
        };
      }),
    ];

    setValue("earnings", updatedEarnings);
    setShowAddRowModal(false);

    calculateVariableValues();
    recalculateOthersValue();
  };

  const removeRow = (index) => {
    const currentRows = watch("earnings");
    setValue(
      "earnings",
      currentRows.filter((_, idx) => idx !== index)
    );
    recalculateOthersValue();
  };

  const calculateVariableValues = () => {
    const earningsArr = getValues("earnings") || [];
    const annualCtc = getValues("annual_ctc") || 0;

    for (let i = 0; i < earningsArr.length; i++) {
      const el = earningsArr[i];

      if (el.calculation_type === "VARIABLE") {
        el.annual_value = roundToTwoDecimalPlaces(
          annualCtc * (el.calculation_value / 100)
        );
        el.monthly_value = roundToTwoDecimalPlaces(el.annual_value / 12);

        setValue(`earnings[${i}].annual_value`, el.annual_value);
        setValue(`earnings[${i}].monthly_value`, el.monthly_value);
      }
    }
  };

  const calculateMonthlyOnAnnualChange = (e, idx) => {
    const annualValue = parseFloat(e.target.value) || 0;
    const monthlyValue = annualValue / 12 || 0;

    const fields = watch("earnings").map(
      (_, i) => `earnings[${i}].annual_value`
    );

    const otherSum = fields
      .filter((f) => f !== `earnings[${idx}].annual_value`)
      .reduce((acc, f) => acc + (parseFloat(getValues(f)) || 0), 0);

    if (otherSum + annualValue <= parseFloat(getValues("annual_ctc"))) {
      setValue(
        `earnings[${idx}].monthly_value`,
        parseFloat(monthlyValue?.toFixed(2))
      );

      setValue(
        `earnings[${idx}].annual_value`,
        parseFloat(annualValue?.toFixed(2))
      );

      recalculateOthersValue();
      return true;
    } else {
      e.preventDefault();
      toast.error("Total components cannot be more than Annual CTC");
      return false;
    }
  };

  const calculateAnnualOnMonthlyChange = (e, idx) => {
    const monthlyValue = parseFloat(e.target.value) || 0;
    const annualValue = monthlyValue * 12 || 0;

    const fields = watch("earnings").map(
      (_, i) => `earnings[${i}].monthly_value`
    );

    const otherSum = fields
      .filter((f) => f !== `earnings[${idx}].monthly_value`)
      .reduce((acc, f) => acc + (parseFloat(getValues(f)) || 0), 0);

    if (otherSum * 12 + annualValue <= parseFloat(getValues("annual_ctc"))) {
      setValue(
        `earnings[${idx}].monthly_value`,
        parseFloat(monthlyValue?.toFixed(2))
      );

      setValue(
        `earnings[${idx}].annual_value`,
        parseFloat(annualValue?.toFixed(2))
      );

      recalculateOthersValue();
    } else {
      toast.error("Total components cannot be more than Annual CTC");
      return false;
    }
  };

  const handleCTCBlur = (e) => {
    const inputValue = parseFloat(e.target.value) || 0;

    const fields =
      watch("earnings").map((_, i) => `earnings[${i}].annual_value`) || [];

    const totalFields = fields.reduce(
      (acc, f) => acc + (parseFloat(getValues(f)) || 0),
      0
    );

    if (inputValue >= totalFields) {
      setValue("annual_ctc", inputValue);
      if (!isEdit) calculateVariableValues();
      recalculateOthersValue();
      return true;
    } else {
      toast.error("Annual CTC cannot be less than total components");
      return false;
    }
  };

  const { handleAuth: saveReq } = usePut({
    url: "user-salary-components/create",
    onSuccess: () => {
      toast.success("User salary components saved successfully");
      navigate(
        "/employee-salary-details/" +
          employeeId +
          "/" +
          username +
          "?source=" +
          source
      );
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "Error saving user salary components: " + error.message
      );
    },
  });
  const isCreating = saveReq.isLoading;

  const { handleAuth: editReq } = usePut({
    url: "user-salary-components/update",
    onSuccess: () => {
      toast.success("User salary components updated successfully");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "Error updating user salary components: " + error.message
      );
    },
  });
  const isEditing = editReq.isLoading;

  const isSaving = isCreating || isEditing;

  const callHandleSave = (data) => {
    const statutoryArr = Object.keys(data.statutory);

    const finalStatutoryArr = statutoryArr.map((key) => {
      const statutoryComponent = statutoryComponentsList.find(
        (sc) => sc.name === key
      );

      return {
        ...statutoryComponent,
        selected: data.statutory[key] || false,
      };
    });

    const monthly_ctc_raw = parseFloat(data.annual_ctc) / 12;
    const final_monthly_ctc = Math.round(monthly_ctc_raw * 100) / 100; // rounding to 2 decimal places

    const reqBody = {
      ...data,
      monthly_ctc: final_monthly_ctc,
      company_employee_id: employeeId,
      template_id: templateId ? templateId : 0,
      from_date: formatDateWithHyphens(data.from_date),
      statutory: finalStatutoryArr,
      monthly_others: othersMonthlyValue,
      annual_others: othersAnnualValue,
    };

    if (isEdit) {
      editReq.mutate({ ...reqBody, salary_id: salaryId });
    } else {
      saveReq.mutate(reqBody);
    }
  };

  return {
    // For form
    templateName,
    control,
    handleSubmit,
    setValue,
    register,
    watch,
    othersMonthlyValue,
    othersAnnualValue,
    handleCTCBlur,
    // For modal
    toggleAddRowModal,
    showAddRowModal,
    addRows,
    removeRow,
    statutoryComponentsList,
    isFetchingStatutory,
    // For saving
    callHandleSave,
    isSaving,
    // Calculation functions
    calculateMonthlyOnAnnualChange,
    calculateAnnualOnMonthlyChange,
  };
};

export default useAddEditUserSalary;
